import * as React from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="w-full overflow-auto">
      <table ref={ref} className={cn('w-full caption-bottom text-body-sm', className)} {...props} />
    </div>
  ),
)
Table.displayName = 'Table'

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b [&_tr]:border-border', className)} {...props} />
))
TableHeader.displayName = 'TableHeader'

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn('border-b border-border transition-colors hover:bg-muted/60', className)}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-4 text-left align-middle text-caption font-normal uppercase tracking-wide text-muted-foreground',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

export interface SortableTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  active: boolean
  direction: 'ascending' | 'descending'
  initialDirection?: 'ascending' | 'descending'
  sortLabel?: string
  onToggle: () => void
}

export const SortableTableHead = React.forwardRef<HTMLTableCellElement, SortableTableHeadProps>(
  (
    {
      active,
      children,
      className,
      direction,
      initialDirection = 'ascending',
      sortLabel,
      onToggle,
      ...props
    },
    ref,
  ) => {
    const nextDirection = active
      ? direction === 'ascending'
        ? 'descending'
        : 'ascending'
      : initialDirection
    const ActiveIcon = direction === 'ascending' ? ArrowUp : ArrowDown

    return (
      <TableHead
        ref={ref}
        aria-sort={active ? direction : undefined}
        className={cn(active && 'text-foreground', className)}
        {...props}
      >
        <button
          type="button"
          className="inline-flex items-center gap-1 text-caption font-normal uppercase tracking-wide"
          aria-label={`Sort ${sortLabel ?? String(children)} ${nextDirection}`}
          onClick={onToggle}
        >
          <span>{children}</span>
          {active ? (
            <ActiveIcon className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <ArrowUpDown
              data-testid="inactive-sort-indicator"
              className="size-3 text-muted-foreground"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
        </button>
      </TableHead>
    )
  },
)
SortableTableHead.displayName = 'SortableTableHead'

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn('px-4 py-2 align-middle text-foreground', className)} {...props} />
))
TableCell.displayName = 'TableCell'
